import React, { useState, useEffect, useRef } from 'react';
import './LiquidarContratosModal.css';

export interface ContratoParaLiquidar {
    id: number;
    numero_contrato: string;
}

type ModalStatus = 'idle' | 'loading' | 'success' | 'error';

interface LiquidarContratosModalProps {
    show: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    contratos: ContratoParaLiquidar[];
    loading?: boolean;
    onSuccess?: () => void;
}

const LiquidarContratosModal: React.FC<LiquidarContratosModalProps> = ({ 
    show, 
    onClose, 
    onConfirm, 
    contratos,
    onSuccess
}) => {
    const [status, setStatus] = useState<ModalStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const prevShowRef = useRef(false);

    useEffect(() => {
        if (show && !prevShowRef.current) {
            setStatus('idle');
            setErrorMessage('');
        }
        prevShowRef.current = show;
    }, [show]);

    const isLoading = status === 'loading';

    const handleConfirm = async () => {
        setStatus('loading');
        setErrorMessage('');
        try {
            await onConfirm();
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setErrorMessage(err instanceof Error ? err.message : 'Erro desconhecido ao liquidar contratos.');
        }
    };

    if (!show) return null;

    return (
        <div className="liquidar-modal-overlay" onClick={isLoading ? undefined : onClose}>
            <div className="liquidar-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="liquidar-modal-header">
                    <h3>Confirmar Liquidação de Contratos</h3>
                    {!isLoading && <button className="liquidar-modal-close" onClick={onClose}>×</button>}
                </div>

                {status === 'loading' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-spinner" />
                        <p className="liquidar-status-text">Processando liquidação...</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-status-icon liquidar-status-icon--success">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M7 12.5L10.5 16L17 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p className="liquidar-status-text liquidar-status-text--success">Contratos liquidados com sucesso!</p>
                        <div className="liquidar-modal-footer">
                            <button className="liquidar-modal-button liquidar-modal-button-confirm" onClick={() => { onClose(); onSuccess?.(); }}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-status-icon liquidar-status-icon--error">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 8L16 16M16 8L8 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="liquidar-status-text liquidar-status-text--error">Erro ao liquidar contratos</p>
                        {errorMessage && <p className="liquidar-status-detail">{errorMessage}</p>}
                        <div className="liquidar-modal-footer">
                            <button className="liquidar-modal-button liquidar-modal-button-cancel" onClick={() => setStatus('idle')}>
                                Tentar novamente
                            </button>
                            <button className="liquidar-modal-button liquidar-modal-button-confirm" onClick={onClose}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'idle' && (
                    <>
                        <div className="liquidar-modal-body">
                            <div className="liquidar-warning-icon">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <p className="liquidar-modal-message">
                                Você está prestes a liquidar <strong>{contratos.length}</strong> {contratos.length === 1 ? 'contrato' : 'contratos'}.
                            </p>
                            <div className="liquidar-contratos-list">
                                <p className="liquidar-list-title">Contratos selecionados:</p>
                                <div className="liquidar-ids-container">
                                    {contratos.map(contrato => (
                                        <span key={contrato.id} className="liquidar-contrato-badge">{contrato.numero_contrato}</span>
                                    ))}
                                </div>
                            </div>
                            <p className="liquidar-modal-warning">
                                Esta ação não pode ser desfeita. Deseja continuar?
                            </p>
                        </div>
                        <div className="liquidar-modal-footer">
                            <button 
                                className="liquidar-modal-button liquidar-modal-button-cancel" 
                                onClick={onClose}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="liquidar-modal-button liquidar-modal-button-confirm" 
                                onClick={handleConfirm}
                            >
                                Confirmar Liquidação
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LiquidarContratosModal;
